/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

export interface IStoreSelectorCrossEntityFiltersProps {
    regions: string[];
    legalEntities: string[];
    selectedRegion?: string;
    selectedLegalEntity?: string;
    inStockOnly: boolean;
    enableInStockOnlyFilter?: boolean;
    allRegionsText: string;
    allLegalEntitiesText: string;
    regionFilterLabel: string;
    legalEntityFilterLabel: string;
    inventoryAvailabilityFilterText: string;
    onRegionChanged(region?: string): void;
    onLegalEntityChanged(legalEntity?: string): void;
    onInStockOnlyChanged(inStockOnly: boolean): void;
}

const ALL_OPTION_VALUE = '';
const REGION_FILTER_ID = 'store-selector-region-filter';
const LEGAL_ENTITY_FILTER_ID = 'store-selector-legal-entity-filter';

/**
 * Renders the Phase 1 PDP Cross-LE filters.
 * @param props - Filter values and handlers.
 * @returns The filter controls.
 */
export const StoreSelectorCrossEntityFilters: React.FC<IStoreSelectorCrossEntityFiltersProps> = props => (
    <div className='ms-store-select__cross-entity-filters'>
        <label className='sr-only' htmlFor={REGION_FILTER_ID}>
            {props.regionFilterLabel}
        </label>
        <select
            id={REGION_FILTER_ID}
            className='ms-store-select__cross-entity-filter ms-store-select__cross-entity-filter-region'
            value={props.selectedRegion ?? ALL_OPTION_VALUE}
            onChange={event => props.onRegionChanged(event.target.value || undefined)}
        >
            <option value={ALL_OPTION_VALUE}>{props.allRegionsText}</option>
            {props.regions.map(region => (
                <option key={region} value={region}>
                    {region}
                </option>
            ))}
        </select>
        <label className='sr-only' htmlFor={LEGAL_ENTITY_FILTER_ID}>
            {props.legalEntityFilterLabel}
        </label>
        <select
            id={LEGAL_ENTITY_FILTER_ID}
            className='ms-store-select__cross-entity-filter ms-store-select__cross-entity-filter-legal-entity'
            value={props.selectedLegalEntity ?? ALL_OPTION_VALUE}
            onChange={event => props.onLegalEntityChanged(event.target.value || undefined)}
        >
            <option value={ALL_OPTION_VALUE}>{props.allLegalEntitiesText}</option>
            {props.legalEntities.map(legalEntity => (
                <option key={legalEntity} value={legalEntity}>
                    {legalEntity}
                </option>
            ))}
        </select>
        {props.enableInStockOnlyFilter !== false ? (
            <label className='ms-store-select__cross-entity-filter ms-store-select__cross-entity-filter-inventory'>
                <input type='checkbox' checked={props.inStockOnly} onChange={event => props.onInStockOnlyChanged(event.target.checked)} />
                <span>{props.inventoryAvailabilityFilterText}</span>
            </label>
        ) : null}
    </div>
);

export default StoreSelectorCrossEntityFilters;
